import { useState } from "react";
import { AlertTriangle, Download, ChevronDown, Wrench, HardHat, Hammer, Layers, Building2, Home, TreePine, Paintbrush, Zap } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import type { LucideIcon } from "lucide-react";
import { materialsCategoryGroups } from "./materials-category-groups";
import "./quote-materials-workspace.css";
import "./materials-order-list.css";

import type { OrderMaterial } from "./materials-order-data";
import { purchaseUnitLabel } from "./material-purchase-units";
export type { OrderMaterial } from "./materials-order-data";

type Props = { included: OrderMaterial[]; optional: OrderMaterial[]; partial: boolean; reference?: string; onJump: (index: number) => void; canDownload?: boolean };

const SECTION_ICONS: Record<string, LucideIcon> = {
  Preparation: HardHat, Demolition: Hammer, Substructure: Layers, Structure: Building2, Roofing: Home,
  "External Works": TreePine, Finishes: Paintbrush, Services: Zap, Other: Wrench,
};

const pence = (n: number) => Math.round(n * 100);
const sumPence = (rows: OrderMaterial[]) => rows.reduce((s, r) => s + pence(r.cost), 0);
const gbpP = (p: number) => `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qty = (n: number) => n.toLocaleString("en-GB", { maximumFractionDigits: 4 });

function safeFilename(reference?: string, format: "csv" | "pdf" = "csv") {
  const base = (reference ?? "").normalize("NFKD").replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^[-.]+|[-.]+$/g, "").slice(0, 80);
  return `${base || "quote"}-order-list.${format}`;
}

function cell(v: string | number) {
  let s = String(v);
  if (typeof v === "string" && /^[\s]*[=+\-@\t\r|%]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) || s !== s.trim() ? `"${s.replace(/"/g, '""')}"` : s;
}

export function materialsOrderCsv(included: OrderMaterial[], optional: OrderMaterial[], partial: boolean) {
  const lines = [["Group", "Category", "Material", "Spec", "Quantity", "Unit", "Estimated cost (GBP)", "Mixed prices", "Used in", "Purchase calculation / checks"].map(cell).join(",")];
  const add = (group: string, rows: OrderMaterial[]) => {
    for (const r of rows) lines.push([group, r.category, r.name, r.spec, Number(r.quantity.toFixed(4)), r.unit,
      (pence(r.cost) / 100).toFixed(2), r.priceVariants > 1 ? `Yes (${r.priceVariants})` : "No",
      r.contributors.map(c => c.name).join("; "), r.purchaseNote ?? ""].map(cell).join(","));
  };
  add("Included", included); add("Optional", optional);
  lines.push(["Included total", "", "", "", "", "", (sumPence(included) / 100).toFixed(2), "", "", ""].map(cell).join(","));
  if (optional.length) lines.push(["Optional total", "", "", "", "", "", (sumPence(optional) / 100).toFixed(2), "", "", ""].map(cell).join(","));
  if (partial) lines.push(cell("Partial list: some work items are not broken down into materials or purchase sizes need checking"));
  if ([...included, ...optional].some(row => row.purchaseNote)) lines.push(cell("Order units are rounded up. Costs remain source material estimates; confirm whole-pack charges with the supplier."));
  return "\uFEFF" + lines.join("\r\n");
}

function Group({ rows, label, testid, onJump }: { rows: OrderMaterial[]; label: string; testid: string; onJump: (i: number) => void }) {
  return <div data-testid={`group-order-${testid}`}>
    <div className="qm-group-heading"><h5>{label}</h5><strong className="tabular-nums" data-testid={`text-order-subtotal-${testid}`}>{gbpP(sumPence(rows))}</strong></div>
    {materialsCategoryGroups(rows).map(g => {
      const Icon = SECTION_ICONS[g.section] ?? Wrench;
      return <section key={g.category} className="qm-category" aria-label={`${label} · ${g.category}`}>
        <h6><Icon size={16} aria-hidden="true" /><span>{g.category}</span></h6>
        {g.rows.map(r => <div key={r.key} className="mol-row" data-testid={`row-order-${r.key}`}>
          <div className="mol-name">
            <strong>{r.name}</strong>
            {r.spec && <div className="mol-spec">{r.spec}</div>}
            {r.purchaseNote && <div className={`mol-spec ${r.needsPackSize ? "text-amber-700 dark:text-amber-400" : ""}`}
              data-testid={`text-purchase-basis-${r.key}`}>{r.needsPackSize && <strong>Purchase size needed. </strong>}{r.purchaseNote}</div>}
            {r.priceVariants > 1 && <span className="mol-mixed" data-testid={`status-mixed-price-${r.key}`}
              title="Cost is summed from different buy prices across work items">Mixed prices ({r.priceVariants})</span>}
          </div>
          <div className="mol-qty" data-testid={`text-order-qty-${r.key}`}>{qty(r.quantity)} {purchaseUnitLabel(r.unit, r.quantity)}</div>
          <div className="mol-cost" data-testid={`text-order-cost-${r.key}`}>{gbpP(pence(r.cost))}</div>
          {r.contributors.length > 0 && <div className="mol-sources"><span>Used in:</span>
            {r.contributors.map((c, i) => <button type="button" key={`${c.index}-${i}`} className="mol-source"
              data-testid={`link-order-source-${r.key}-${c.index}`} onClick={() => onJump(c.index)}>{c.name}</button>)}
          </div>}
        </div>)}
      </section>;
    })}
  </div>;
}

export function MaterialsOrderList({ included, optional, partial, reference, onJump, canDownload = false }: Props) {
  const [exporting, setExporting] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const total = sumPence(included);
  const empty = included.length === 0 && optional.length === 0;
  const download = async (format: "csv" | "pdf") => {
    if (!canDownload || exporting) return;
    setDownloadError(null);
    setExporting(true);
    try {
      const blob = format === "csv"
        ? new Blob([materialsOrderCsv(included, optional, partial)], { type: "text/csv;charset=utf-8" })
        : (await import("./materials-order-pdf")).buildMaterialsOrderPdf(included, optional, partial, reference).output("blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = safeFilename(reference, format);
      try { document.body.appendChild(a); a.click(); }
      finally {
        a.remove();
        // Safari/mobile browsers may not have consumed the URL immediately.
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      }
    } catch {
      setDownloadError(`Could not create the ${format.toUpperCase()} download. Please try again.`);
    } finally {
      setExporting(false);
    }
  };
  return <section className="qm-panel mol-panel" data-testid="materials-order-list" aria-labelledby="mol-title">
    <div className="mol-heading">
      <span className="qm-step" aria-hidden="true">03</span>
      <h4 id="mol-title" className="qm-panel-title" style={{ fontSize: 15, fontWeight: 600 }}>Materials Order List</h4>
      <span className="qm-private-tag">Builder only</span>
      <span className="mol-total" data-testid="text-order-total" aria-label="Estimated procurement cost">{gbpP(total)}</span>
      {!empty && <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="mol-download disabled:opacity-50" disabled={!canDownload || exporting}
            aria-label="Download" aria-busy={exporting}
            title={canDownload ? "Download consolidated materials" : "Wait for current changes to finish"}
            data-testid="button-download-materials">
            <Download size={13} aria-hidden="true" />Download<ChevronDown size={13} aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => { void download("csv"); }}>Download as CSV</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => { void download("pdf"); }}>Download as PDF</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>}
    </div>
    <p className="qm-scope-note">Read-only order list from your confirmed Materials List. Quantities are consolidated across work items; costs are estimated procurement only, not customer prices.</p>
    {[...included, ...optional].some(row => row.purchaseNote) && <p className="qm-scope-note">
      Order quantities are rounded up to whole purchase units where the size is known. Costs remain the source material estimates; confirm whole-pack charges with your supplier.
    </p>}
    {!canDownload && !empty && <p className="qm-scope-note">Downloads will be available when current changes finish.</p>}
    {downloadError && <p className="qm-scope-note text-destructive" role="alert">{downloadError}</p>}
    {partial && <p className="mol-partial" role="status" data-testid="status-order-partial">
      <AlertTriangle size={15} aria-hidden="true" />Partial list. Some work items need materials approval or a breakdown, or purchase sizes still need checking. Review the flagged items before ordering.
    </p>}
    <div className="qm-panel-content space-y-4 min-w-0">
      {included.length > 0
        ? <Group rows={included} label="Included" testid="included" onJump={onJump} />
        : <p className="mol-empty" data-testid="text-order-empty">No included materials to order.</p>}
      {optional.length > 0 && <div className="mol-optional">
        <p className="mol-optional-note">Optional extras. Order only if the customer accepts them. Not in the total above.</p>
        <Group rows={optional} label="Optional items" testid="optional" onJump={onJump} />
      </div>}
    </div>
  </section>;
}
